/**
 * Turning what was read from CSRD into pieces to import.
 *
 * The same pieces a Word import makes, so the same review follows: a list
 * shaped like the statement, a preview of each piece as it would arrive, and
 * nothing added that somebody did not tick.
 *
 * The shape follows the statement rather than CSRD's menu:
 *
 *   heading 1   the standard           E1 · Climate change
 *   heading 2   the disclosure         E1-5 Energy consumption and mix
 *   heading 3   a narrative's label    followed by its text
 *   table       a table of figures     with where it came from underneath
 *
 * **Only the disclosure's heading is tagged.** The tag is what the disclosure
 * index is built from, and one entry per requirement pointing at where it
 * starts is what somebody looking for `E1-5` wants. The standard's heading is
 * left untagged on purpose, for the reason the CSRD template gives: whether a
 * topic is material is the author's conclusion, not something an import gets
 * to declare.
 *
 * Every narrative is sanitised on the way through. It was typed into another
 * application, and it arrives here as HTML.
 */

import type { CsrdBlock, CsrdCell, CsrdContent, CsrdDisclosure } from '@/api/csrd'
import { readTag } from '@/domain/esrs'
import { sanitiseHtml, textOf } from '@/domain/sanitise'
import {
  createSection,
  newId,
  type CsrdLink,
  type HeadingSection,
  type TableCell,
  type TableSection,
  type TextSection,
} from '@/domain/section'
import { proseFrom, type ImportPart, type ImportPiece } from '@/features/import/word'
import { readCellAlign, readCellColour } from '@/sections/table/edit'

/** What the import produced, and what it had nothing for. */
export interface CsrdImport {
  readonly pieces: readonly ImportPiece[]
  /** Disclosures that were asked for and had nothing collected in them. */
  readonly empty: readonly CsrdDisclosure[]
}

/**
 * A disclosure's name without CSRD's internal number.
 *
 * CSRD labels a role type `[301050] E1-5 Energy consumption and mix`. The
 * bracketed number is the taxonomy's role code, which no reader of a report
 * has any use for.
 */
export function disclosureTitle(label: string): string {
  return label.replace(/^\s*\[[^\]]*\]\s*/, '').trim() || label.trim()
}

/** How a standard is named in a heading: `ESRS 2 · General disclosures`. */
export function standardTitle(standard: string, standardLabel: string): string {
  const code = standard.replace(/^ESRS\s*(\d)$/i, 'ESRS $1')
  return standardLabel && standardLabel !== standard ? `${code} · ${standardLabel}` : code
}

/**
 * The paragraphs of a narrative.
 *
 * Top-level blocks become parts, as in a Word import, so an internal note
 * left in a narrative can be dropped on its own. Bare text between them, which
 * is what a plain-text field holds, is put in a paragraph of its own rather
 * than lost.
 */
export function partsOf(html: string): ImportPart[] {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const parts: ImportPart[] = []

  for (const node of [...parsed.body.childNodes]) {
    let markup: string
    if (node.nodeType === Node.TEXT_NODE) {
      const words = (node.textContent ?? '').trim()
      if (!words) continue
      const paragraph = parsed.createElement('p')
      paragraph.textContent = words
      markup = paragraph.outerHTML
    } else if (node instanceof Element) {
      markup = node.outerHTML
    } else {
      continue
    }

    const safe = sanitiseHtml(markup)
    const text = textOf(safe)
    if (text.trim() !== '') parts.push({ id: newId(), html: safe, text })
  }

  return parts
}

/**
 * One cell, with whatever a design template set on it.
 *
 * A background or an alignment is kept only when it is one a table here can
 * hold, and a colour only when it is safe to write into a stylesheet: it came
 * from a spreadsheet somebody uploaded to another application.
 */
export function cellFrom(cell: CsrdCell): TableCell {
  const made: TableCell = { value: cell.value }
  if (cell.numeric) made.numeric = true
  if (cell.strong) made.strong = true
  const fill = readCellColour(cell.fill)
  const align = readCellAlign(cell.align)
  if (fill) made.fill = fill
  if (align) made.align = align
  return made
}

function heading(title: string, level: 1 | 2 | 3, depth: number): ImportPiece {
  const base = createSection('heading', title) as HeadingSection
  return { id: base.id, section: { ...base, level }, depth, parts: [] }
}

function blockPieces(block: CsrdBlock, caption: string, link: CsrdLink | undefined): ImportPiece[] {
  if (block.kind === 'table') {
    const base = createSection('table', block.title) as TableSection
    const table: TableSection = {
      ...base,
      header: block.header.map(cellFrom),
      rows: block.rows.map((row) => row.map(cellFrom)),
      caption,
      ...(link ? { csrd: link } : {}),
    }
    return [{ id: table.id, section: table, depth: 2, parts: [] }]
  }

  const parts = partsOf(block.html)
  if (parts.length === 0) return []

  const base = createSection('text') as TextSection
  const { html, title } = proseFrom(parts)
  return [
    heading(block.title, 3, 2),
    { id: base.id, section: { ...base, title, html }, depth: 3, parts },
  ]
}

/** Where one of a disclosure's tables came from, or nothing when it came from no reporting. */
function linkFor(
  content: CsrdContent,
  disclosure: CsrdDisclosure,
  tableIndex: number,
  language: string,
): CsrdLink | undefined {
  if (!content.reporting) return undefined
  return {
    entityId: content.entity.id,
    entityCode: content.entity.code,
    roleTypeId: disclosure.id,
    roleCode: disclosure.code,
    ref: disclosure.ref,
    disclosure: disclosureTitle(disclosure.label),
    tableIndex,
    reportingCode: content.reporting.code,
    reportingLabel: content.reporting.label,
    language: language === 'fr' || language === 'en' ? language : '',
    pulledAt: content.pulledAt,
    collected: disclosure.collected ?? 0,
    controlled: disclosure.controlled ?? 0,
    audited: disclosure.audited ?? 0,
  }
}

/**
 * Pieces from what CSRD said.
 *
 * `caption` is written under every table, and says where the figures came
 * from. It arrives already worded, because the words belong to the language
 * the person is working in and this file does not know which that is.
 */
export function piecesFromCsrd(content: CsrdContent, caption: string, language = ''): CsrdImport {
  const empty: CsrdDisclosure[] = []

  // Grouped by standard, in the order the standards first appear. A choice
  // made across the list can interleave them, and a statement that returns to
  // E1 after S1 is one somebody has to reassemble by hand.
  const byStandard = new Map<string, { title: string; pieces: ImportPiece[] }>()

  for (const disclosure of content.disclosures) {
    // Each table keeps where it came from, so it can be read again. Without a
    // reporting there is nothing to tie it to, and it stays an ordinary table.
    let tableIndex = 0
    const blocks = disclosure.blocks.flatMap((block) => {
      const link =
        block.kind === 'table' ? linkFor(content, disclosure, tableIndex++, language) : undefined
      return blockPieces(block, caption, link)
    })
    if (blocks.length === 0) {
      empty.push(disclosure)
      continue
    }

    let group = byStandard.get(disclosure.standard)
    if (!group) {
      group = {
        title: standardTitle(disclosure.standard, disclosure.standardLabel),
        pieces: [],
      }
      byStandard.set(disclosure.standard, group)
    }

    const opening = heading(disclosureTitle(disclosure.label), 2, 1)
    const tag = readTag(disclosure.ref, 'material')
    group.pieces.push(tag ? { ...opening, section: { ...opening.section, tag } } : opening)
    group.pieces.push(...blocks)
  }

  const pieces = [...byStandard.values()].flatMap((group) => [
    heading(group.title, 1, 0),
    ...group.pieces,
  ])

  return { pieces, empty }
}
